import type { ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';
import type { IpdOrderPriority } from '@his/shared';
import { IPD_ORDER_PRIORITY } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { DateTimePicker } from '@/components/ui/primitives';
import { ChoiceButtons, type ChoiceOption, type ChoiceTone } from '@/components/ui/choice-buttons';
import { localDateTimeValue } from '../../dose-timing';

interface GroupAria {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
}

function chipClass(selected: boolean, tone: ChoiceTone = 'default'): string {
  return cn(
    'focus-ring inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    selected
      ? tone === 'destructive'
        ? 'border-destructive/50 bg-destructive/10 font-medium text-destructive'
        : tone === 'warning'
          ? 'border-warning/50 bg-warning/10 font-medium text-warning'
          : 'border-primary/50 bg-primary/10 font-medium text-primary'
      : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
  );
}

function labelledBy(id: string | undefined, label: string | undefined): string | undefined {
  return label || !id ? undefined : `${id}-label`;
}

export function ToggleChips<T extends string>({
  values,
  options,
  onChange,
  className,
  ...aria
}: GroupAria & {
  values: readonly T[];
  options: readonly ChoiceOption<T>[];
  onChange: (values: T[]) => void;
  className?: string;
}) {
  const toggle = (value: T) => {
    const next = values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
    onChange(options.map((option) => option.value).filter((entry) => next.includes(entry)));
  };
  return (
    <div
      id={aria.id}
      role="group"
      aria-label={aria['aria-label']}
      aria-labelledby={labelledBy(aria.id, aria['aria-label'])}
      aria-describedby={aria['aria-describedby']}
      className={cn('flex flex-wrap gap-1.5', className)}
    >
      {options.map((option) => {
        const selected = values.includes(option.value);
        return (
          <button key={option.value} type="button" aria-pressed={selected} onClick={() => toggle(option.value)} className={chipClass(selected, option.tone)}>
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Stepper({
  value,
  min,
  max,
  onChange,
  ...aria
}: GroupAria & {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const set = (next: number) => onChange(Math.min(max, Math.max(min, next)));
  const button = 'focus-ring grid h-9 w-9 place-items-center rounded-md border border-input bg-background text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div
      id={aria.id}
      role="group"
      aria-label={aria['aria-label']}
      aria-labelledby={labelledBy(aria.id, aria['aria-label'])}
      aria-describedby={aria['aria-describedby']}
      className="inline-flex items-center gap-2"
    >
      <button type="button" className={button} disabled={value <= min} onClick={() => set(value - 1)} aria-label={tr('ipd', 'io_form_less')}>
        <Minus className="h-4 w-4" aria-hidden />
      </button>
      <output aria-live="polite" className="min-w-[2.5rem] text-center text-base font-semibold tabular-nums">
        {value}
      </output>
      <button type="button" className={button} disabled={value >= max} onClick={() => set(value + 1)} aria-label={tr('ipd', 'io_form_more')}>
        <Plus className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export function isoFromLocal(local: string): string {
  if (!local) return '';
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
}

export function localFromIso(iso: string): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '' : localDateTimeValue(parsed);
}

export function InstantPicker({
  value,
  onChange,
  min,
  ...aria
}: GroupAria & {
  value: string;
  onChange: (iso: string) => void;
  min?: string;
}) {
  return (
    <DateTimePicker
      id={aria.id}
      className="w-full"
      value={localFromIso(value)}
      min={min ? localFromIso(min) : undefined}
      onChange={(local) => onChange(isoFromLocal(local))}
      aria-describedby={aria['aria-describedby']}
      aria-invalid={aria['aria-invalid']}
      aria-label={aria['aria-label']}
    />
  );
}

const PRIORITY_TONE: Record<IpdOrderPriority, ChoiceTone> = { routine: 'default', urgent: 'warning', stat: 'destructive' };

export function PriorityButtons({ value, onChange, ...aria }: GroupAria & { value: IpdOrderPriority; onChange: (value: IpdOrderPriority) => void }) {
  return (
    <ChoiceButtons
      id={aria.id}
      value={value}
      onChange={onChange}
      label={tr('ipd', 'io_form_priority')}
      aria-describedby={aria['aria-describedby']}
      options={IPD_ORDER_PRIORITY.map((priority) => ({ value: priority, label: tr('ipd', `io_priority_${priority}`), tone: PRIORITY_TONE[priority] }))}
    />
  );
}

export function ComputedValue({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm font-medium tabular-nums">{children}</p>;
}
